import React from "react";
import { cn } from "../../utils/cn";

export type ContainerVariant = "default" | "white" | "gray" | "dashboard";

interface FullHeightContainerProps extends React.ComponentProps<"div"> {
  as?: React.ElementType;
  /**
   * Background color variant
   * - default: transparent
   * - white: bg-white
   * - gray: bg-gray-50
   * - dashboard: bg-gray-50 (alias for gray)
   */
  variant?: ContainerVariant;
  /**
   * Whether this container handles its own scrolling.
   * If true, it adds overflow-y-auto and strict height constraints.
   * If false, it grows to fit content (relies on parent scrolling).
   * @default false
   */
  isScrollable?: boolean;
}

const variantStyles: Record<ContainerVariant, string> = {
  default: "",
  white: "bg-white",
  gray: "bg-gray-50",
  dashboard: "bg-gray-50",
};

/**
 * FullHeightContainer - A wrapper that ensures the content fills the viewport height.
 *
 * Architecture Note:
 * Use `isScrollable={true}` when this container should handle scrolling (e.g. Dashboard, Lists).
 * This ensures the background color covers the 'overscroll' area.
 */
export const FullHeightContainer = React.forwardRef<
  HTMLElement,
  FullHeightContainerProps
>(
  (
    {
      className,
      children,
      as: Component = "div",
      variant = "default",
      isScrollable = false,
      ...props
    },
    ref,
  ) => {
    return (
      <Component
        ref={ref}
        className={cn(
          "w-full flex flex-col flex-1",
          // Height calculation depends on scroll mode
          isScrollable
            ? "h-[calc(100vh_-_60px)] sm:h-[calc(100vh_-_70px)] overflow-y-auto" // Fixed height for internal scroll
            : "min-h-[calc(100vh_-_60px)] sm:min-h-[calc(100vh_-_70px)]", // Min-height for external scroll
          variantStyles[variant],
          className,
        )}
        {...props}
      >
        {children}
      </Component>
    );
  },
);

FullHeightContainer.displayName = "FullHeightContainer";
